import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { formatLocalDate } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, DollarSign, Search, X, Loader2, Send } from "lucide-react";
import { SiPaypal } from "react-icons/si";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useIsMobile } from "@/hooks/use-mobile";
import type { AccountsPayable, Talent } from "@shared/schema";

const formatCurrency = (amount: string | number | null | undefined): string => {
  const num = parseFloat(String(amount || "0"));
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(num);
};

const formatDate = (date: string | Date | null | undefined): string =>
  formatLocalDate(date, { year: "numeric", month: "short", day: "numeric" });

export function AccountsPayableTab() {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<AccountsPayable | null>(null);
  const [formData, setFormData] = useState({
    payeeType: "contractor" as "contractor" | "talent" | "vendor" | "other",
    payeeName: "",
    payeeEmail: "",
    talentId: "",
    description: "",
    amount: "",
    dueDate: "",
    status: "pending" as "pending" | "paid" | "overdue",
    paymentMethod: "",
    paymentReference: "",
    notes: "",
  });

  const { data: payables = [], isLoading } = useQuery<AccountsPayable[]>({
    queryKey: ["/api/billing/payables"],
  });

  const { data: talent = [] } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await apiRequest("POST", "/api/billing/payables", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/payables"] });
      toast({ title: "Success", description: "Expense logged successfully." });
      resetForm();
      setIsAddDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<typeof formData> }) => {
      const response = await apiRequest("PATCH", `/api/billing/payables/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/payables"] });
      toast({ title: "Success", description: "Entry updated successfully." });
      resetForm();
      setIsEditDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/billing/payables/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/payables"] });
      toast({ title: "Success", description: "Entry deleted successfully." });
      setDeleteConfirmOpen(false);
      setSelectedEntry(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const paypalPayoutMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", `/api/billing/payables/${id}/paypal-payout`);
      return response.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/payables"] });
      toast({ 
        title: "PayPal Payout Sent", 
        description: data.batchId ? `Batch ID: ${data.batchId}` : "Payment sent successfully." 
      });
    },
    onError: (error: Error) => {
      toast({ title: "PayPal Payout Failed", description: error.message, variant: "destructive" });
    },
  });

  const resetForm = () => {
    setFormData({
      payeeType: "contractor",
      payeeName: "",
      payeeEmail: "",
      talentId: "",
      description: "",
      amount: "",
      dueDate: "",
      status: "pending",
      paymentMethod: "",
      paymentReference: "",
      notes: "",
    });
    setSelectedEntry(null);
  };

  const openEditDialog = (entry: AccountsPayable) => {
    setSelectedEntry(entry);
    setFormData({
      payeeType: entry.payeeType as "contractor" | "talent" | "vendor" | "other",
      payeeName: entry.payeeName || "",
      payeeEmail: entry.payeeEmail || "",
      talentId: entry.talentId || "",
      description: entry.description || "",
      amount: entry.amount || "",
      dueDate: entry.nextPaymentDate ? new Date(entry.nextPaymentDate).toISOString().split("T")[0] : "",
      status: entry.status as "pending" | "paid" | "overdue",
      paymentMethod: entry.paymentMethod || "",
      paymentReference: entry.paymentReference || "",
      notes: entry.notes || "",
    });
    setIsEditDialogOpen(true);
  };

  const filteredPayables = payables.filter((entry) => {
    const matchesSearch =
      !searchQuery ||
      entry.payeeName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || entry.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPending = filteredPayables
    .filter((e) => e.status === "pending")
    .reduce((sum, e) => sum + parseFloat(e.amount || "0"), 0);

  const totalPaid = filteredPayables
    .filter((e) => e.status === "paid")
    .reduce((sum, e) => sum + parseFloat(e.amount || "0"), 0);

  const totalOverdue = filteredPayables
    .filter((e) => e.status === "overdue")
    .reduce((sum, e) => sum + parseFloat(e.amount || "0"), 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Payments</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600" data-testid="text-pending-payables">
              {formatCurrency(totalPending)}
            </div>
            <p className="text-xs text-muted-foreground">
              {filteredPayables.filter((e) => e.status === "pending").length} pending entries
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paid This Period</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600" data-testid="text-paid-payables">
              {formatCurrency(totalPaid)}
            </div>
            <p className="text-xs text-muted-foreground">
              {filteredPayables.filter((e) => e.status === "paid").length} paid entries
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
            <DollarSign className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive" data-testid="text-overdue-payables">
              {formatCurrency(totalOverdue)}
            </div>
            <p className="text-xs text-muted-foreground">
              {filteredPayables.filter((e) => e.status === "overdue").length} overdue entries
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <CardTitle>Accounts Payable</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Track expenses owed to contractors, talent, and vendors
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-full sm:w-56"
                data-testid="input-search-payables"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 no-default-hover-elevate"
                  onClick={() => setSearchQuery("")}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[130px]" data-testid="select-payables-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setIsAddDialogOpen(true)} data-testid="button-add-payable" className="w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Log Expense
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {filteredPayables.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No payable entries found</p>
              <p className="text-sm">Click "Log Expense" to add your first entry</p>
            </div>
          ) : isMobile ? (
            <div className="space-y-2">
              {filteredPayables.map((entry) => (
                <div key={entry.id} className="rounded-lg border border-border p-3" data-testid={`row-payable-${entry.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div>
                      <span className="font-medium text-sm">{entry.payeeName || "N/A"}</span>
                      <div className="text-xs text-muted-foreground">{entry.description || "N/A"}</div>
                    </div>
                    <span className="font-bold text-sm shrink-0">{formatCurrency(entry.amount)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1.5">
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="capitalize text-xs">{entry.payeeType}</Badge>
                      <Badge
                        variant={entry.status === "paid" ? "default" : entry.status === "overdue" ? "destructive" : "secondary"}
                        className="text-xs"
                      >
                        {entry.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {entry.status === "pending" && entry.payeeEmail && (
                        <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => paypalPayoutMutation.mutate(entry.id)} disabled={paypalPayoutMutation.isPending} data-testid={`button-paypal-payout-${entry.id}`}>
                          {paypalPayoutMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <SiPaypal className="h-4 w-4 text-blue-600" />}
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => openEditDialog(entry)} data-testid={`button-edit-payable-${entry.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => { setSelectedEntry(entry); setDeleteConfirmOpen(true); }} data-testid={`button-delete-payable-${entry.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {entry.nextPaymentDate && <div className="text-xs text-muted-foreground mt-1">Due: {formatDate(entry.nextPaymentDate)}</div>}
                </div>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payee</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPayables.map((entry) => (
                    <TableRow key={entry.id} data-testid={`row-payable-${entry.id}`}>
                      <TableCell className="font-medium">{entry.payeeName || "N/A"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {entry.payeeType}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {entry.description || "N/A"}
                      </TableCell>
                      <TableCell>{formatCurrency(entry.amount)}</TableCell>
                      <TableCell>{formatDate(entry.nextPaymentDate)}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            entry.status === "paid"
                              ? "default"
                              : entry.status === "overdue"
                              ? "destructive"
                              : "secondary"
                          }
                        >
                          {entry.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {entry.status === "pending" && entry.payeeEmail && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => paypalPayoutMutation.mutate(entry.id)}
                            disabled={paypalPayoutMutation.isPending}
                            title="Pay via PayPal"
                            data-testid={`button-paypal-payout-${entry.id}`}
                          >
                            {paypalPayoutMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <SiPaypal className="h-4 w-4 text-blue-600" />
                            )}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(entry)}
                          data-testid={`button-edit-payable-${entry.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedEntry(entry);
                            setDeleteConfirmOpen(true);
                          }}
                          data-testid={`button-delete-payable-${entry.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Log Expense</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payee Type</Label>
                <Select
                  value={formData.payeeType}
                  onValueChange={(v) => setFormData({ ...formData, payeeType: v as any })}
                >
                  <SelectTrigger data-testid="select-payee-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contractor">Contractor</SelectItem>
                    <SelectItem value="talent">Talent</SelectItem>
                    <SelectItem value="vendor">Vendor</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.payeeType === "talent" ? (
                <div className="space-y-2">
                  <Label>Talent</Label>
                  <Select
                    value={formData.talentId}
                    onValueChange={(v) => {
                      const ent = talent.find((e) => e.id === v);
                      setFormData({
                        ...formData,
                        talentId: v,
                        payeeName: ent?.name || "",
                      });
                    }}
                  >
                    <SelectTrigger data-testid="select-talent">
                      <SelectValue placeholder="Select talent" />
                    </SelectTrigger>
                    <SelectContent>
                      {talent.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Payee Name</Label>
                  <Input
                    value={formData.payeeName}
                    onChange={(e) => setFormData({ ...formData, payeeName: e.target.value })}
                    placeholder="Enter payee name"
                    data-testid="input-payee-name"
                  />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Payee Email (for PayPal payouts)</Label>
              <Input
                type="email"
                value={formData.payeeEmail}
                onChange={(e) => setFormData({ ...formData, payeeEmail: e.target.value })}
                placeholder="payee@example.com"
                data-testid="input-payee-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Payment description"
                data-testid="input-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="input-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  data-testid="input-due-date"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
                data-testid="input-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                setIsAddDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate(formData)}
              disabled={!formData.payeeName || !formData.amount || createMutation.isPending}
              data-testid="button-save-payable"
            >
              {createMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Expense</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payee Type</Label>
                <Select
                  value={formData.payeeType}
                  onValueChange={(v) => setFormData({ ...formData, payeeType: v as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contractor">Contractor</SelectItem>
                    <SelectItem value="talent">Talent</SelectItem>
                    <SelectItem value="vendor">Vendor</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({ ...formData, status: v as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Payee Name</Label>
              <Input
                value={formData.payeeName}
                onChange={(e) => setFormData({ ...formData, payeeName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Payee Email (for PayPal payouts)</Label>
              <Input
                type="email"
                value={formData.payeeEmail}
                onChange={(e) => setFormData({ ...formData, payeeEmail: e.target.value })}
                placeholder="payee@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                />
              </div>
            </div>
            {formData.status === "paid" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Payment Method <span className="text-destructive">*</span></Label>
                  <Select
                    value={formData.paymentMethod}
                    onValueChange={(v) => setFormData({ ...formData, paymentMethod: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Reference #</Label>
                  <Input
                    value={formData.paymentReference}
                    onChange={(e) => setFormData({ ...formData, paymentReference: e.target.value })}
                    placeholder="Transaction ID"
                  />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                setIsEditDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                selectedEntry && updateMutation.mutate({ id: selectedEntry.id, data: formData })
              }
              disabled={updateMutation.isPending || (formData.status === "paid" && !formData.paymentMethod)}
            >
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Entry</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this expense entry? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedEntry && deleteMutation.mutate(selectedEntry.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
